import React, { useState } from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Circle,
  HStack,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import RecipientSelectionStep from './RecipientSelectionStep';
import ContentEditorStep from './ContentEditorStep';
import ReviewSendStep from './ReviewSendStep';
import { sendEmail } from '../../api/auth/mail';

const steps = [
  { title: '대상 선택', description: '수신자 선택' },
  { title: '콘텐츠 작성', description: '메일 내용 작성' },
  { title: '검토 및 발송', description: '최종 확인' },
];

const QuickSendWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const toast = useToast();

  const [recipientData, setRecipientData] = useState({
    filter: null,
    recipients: [],
    count: 0,
  });
  const [contentData, setContentData] = useState({
    subject: '',
    html: '',
    from: 'support@appplatform.co.kr',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };
  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleRecipientComplete = data => {
    setRecipientData(data);
    handleNext();
  };
  const handleContentComplete = data => {
    setContentData(data);
    handleNext();
  };

  const handleSend = async options => {
    setIsSubmitting(true);
    try {
      const payload = {
        subject: contentData.subject,
        html: contentData.html,
        filter: recipientData.filter,
        recipients:
          recipientData.recipients.length > 0
            ? recipientData.recipients
            : undefined,
        scheduleAt: options?.scheduleAt || undefined,
      };
      const result = await sendEmail(payload);

      toast({
        title: '메일 발송 성공',
        description: `RequestId: ${result.requestId}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setRecipientData({ filter: null, recipients: [], count: 0 });
      setContentData({
        subject: '',
        html: '',
        from: 'support@appplatform.co.kr',
      });
      setActiveStep(0);
    } catch (error) {
      toast({
        title: '메일 발송 실패',
        description:
          error.response?.data?.message || '메일 발송 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Heading mb={8}>메일 발송</Heading>

      <HStack spacing={4} mb={8} justify="center">
        {steps.map((step, index) => (
          <Flex key={index} align="center">
            <VStack spacing={1}>
              <Circle
                size="40px"
                bg={index <= activeStep ? 'purple.500' : 'gray.200'}
                color="white"
              >
                {index < activeStep ? (
                  <CheckIcon />
                ) : (
                  <Text fontWeight="bold">{index + 1}</Text>
                )}
              </Circle>
              <VStack spacing={0}>
                <Text
                  fontSize="sm"
                  fontWeight={index === activeStep ? 'bold' : 'normal'}
                >
                  {step.title}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {step.description}
                </Text>
              </VStack>
            </VStack>
            {index < steps.length - 1 && (
              <Box w="60px" h="2px" bg="gray.300" mx={2} />
            )}
          </Flex>
        ))}
      </HStack>

      <Box bg="white" p={6} borderRadius="md" boxShadow="sm">
        {activeStep === 0 && (
          <RecipientSelectionStep
            initialData={recipientData}
            onComplete={handleRecipientComplete}
          />
        )}
        {activeStep === 1 && (
          <ContentEditorStep
            initialData={contentData}
            onComplete={handleContentComplete}
            onBack={handleBack}
          />
        )}
        {activeStep === 2 && (
          <ReviewSendStep
            recipientData={recipientData}
            contentData={contentData}
            onSend={handleSend}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        )}
      </Box>
    </Container>
  );
};

export default QuickSendWizard;
